import React, { ElementType, ComponentPropsWithoutRef, forwardRef, memo } from 'react';
import styles from './typography.module.css';
import clsx from 'clsx';

type Variant =
    | 'display1'
    | 'display2'
    | 'display3'
    | 'display4'
    | 'display5'
    | 'display6'
    | 'body'
    | 'bodySm'
    | 'bodyXs'
    | 'caption'
    | 'link'
    | 'quote'
    | 'overline';


const DEFAULT_TYPE = 'div';

export type TextAlign = 'left' | 'center' | 'right';

const getTagFromVariant = (variant?: Variant): ElementType => {
    if (!variant) {
        return DEFAULT_TYPE;
    }
    switch (variant) {
        case 'display1':
            return 'h1';
        case 'display2':
            return 'h2';
        case 'display3':
            return 'h3';
        case 'display4':
            return 'h4';
        case 'display5':
            return 'h5';
        case 'display6':
            return 'h6';
        case 'body':
        case 'bodySm':
            return 'p';
        case 'caption':
            return 'span';
        case 'link':
            return 'a';
        case 'quote':
            return 'blockquote';
        case 'overline':
            return 'span';
        default:
            return DEFAULT_TYPE;
    }

};

type TypographyProps<T extends ElementType> = {
    as?: T;
    variant?: Variant;
    isDarkBackground?: boolean;
    children?: React.ReactNode;

    textAlign?: TextAlign;
    uppercase?: boolean;
    nowrap?: boolean;
    overflowEllipsis?: boolean;
    lineThrough?: boolean;
    underline?: boolean;
    color?: 'green' | 'blue' | 'red' | 'yellow';
} & ComponentPropsWithoutRef<T>;


const TypographyComponent = forwardRef(
    <T extends ElementType = typeof DEFAULT_TYPE>(
        props: TypographyProps<T>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref: React.ForwardedRef<any>
    ) => {
        const {
            as,
            variant,
            textAlign,
            nowrap,
            overflowEllipsis,
            lineThrough,
            underline,
            uppercase,
            isDarkBackground = false,
            className,
            color = null,
            ...restProps
        } = props;

        const Component = as ?? getTagFromVariant(variant);

        return (
            <Component
                className={clsx(
                    className, 
                    styles[`variant-${variant}`],
                    {
                        [styles[`${textAlign}Align`]]: textAlign,
                        [styles.nowrap]: nowrap,
                        [styles.overflowEllipsis]: overflowEllipsis,
                        [styles.lineThrough]: lineThrough,
                        [styles.underline]: underline,
                        [styles.isDarkBackground]: isDarkBackground,
                        [styles.textTransformUppercase]: uppercase,
                        [styles[`${color}Color`]]: color,
                    }
                )}
                ref={ref}
                {...restProps}
            />
        );
    }
);

const Typography = memo(TypographyComponent);
Typography.displayName = 'Typography';

export { Typography };    
export type { TypographyProps };

